/**
 * The media library — catalogue and pandit imagery, and nothing else.
 *
 * **Completion evidence and KYC documents never appear here.** That is the server's rail, not
 * this screen's filter: `/v1/admin/media` is constrained to the `catalog` and `pandit` prefixes
 * and refuses any other with a 422. The prefix control offers only the two it may ask for, and
 * the header says the boundary out loud — because an operator who cannot find a completion photo
 * here needs to know it is in the booking, not that it does not exist.
 *
 * **Every filter is a server filter.** The grid is paged, so narrowing after the fetch would
 * narrow one page of an unknown many — "3 results" when the true answer is "3 on this page".
 *
 * **A tile shows what it is.** While an object is still processing, the preview endpoint hands
 * back the original in place of a derivative and says so; the tile is labelled accordingly rather
 * than pretending the crop exists. An object with no bytes yet is `pending` and shows no image at
 * all — a broken thumbnail reads as a failure when it is only work in progress.
 *
 * **No text is ever baked into an image.** Hindi strings run 25–30% wider than their English
 * equivalents, so text must be live markup over the image.
 */
import { useState } from 'react';
import { useConsole } from '../app/console';
import { ScanHealth } from './ScanHealth';
import { StatusChip } from '../components/primitives';
import { DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { EmptyState, ErrorState, LoadingState, Modal } from '../components/feedback';
import { Select } from '../components/forms';
import {
  ConfirmAction,
  Consequences,
  FilterBar,
  ScopeAction,
  Section,
  StatTile,
} from '../components/kit';
import { Button } from '../components/primitives';
import { errorMessage } from '../lib/api/client';
import {
  useDeleteMedia,
  useMediaLibrary,
  useMediaPreview,
  useMediaUsages,
  useReplaceMediaUsages,
  type MediaLibraryRow,
  type MediaUsageRef,
  type MediaUsageRow,
} from '../lib/api/hooks/media';
import { usePujas } from '../lib/api/hooks/catalogue';
import { useUrlSearch } from '../lib/filterCodecs';
import { PujaImageUpload } from './PujaImageUpload';
import { formatDateTime } from '../lib/datetime';

const PAGE_SIZE = 24;

/** Only the two the server will accept. A third option here would be a 422 with a nice label. */
const PREFIXES = ['All imagery', 'Catalogue', 'Pandit photos'] as const;
const PREFIX_VALUES: Record<string, string | undefined> = {
  'All imagery': undefined,
  Catalogue: 'catalog',
  'Pandit photos': 'pandit',
};

const STATUSES = ['Any status', 'pending', 'uploaded', 'scanning', 'ready', 'rejected'] as const;

export function statusTone(status: string): 'neutral' | 'warn' | 'success' | 'error' {
  if (status === 'ready') return 'success';
  if (status === 'rejected') return 'error';
  if (status === 'pending' || status === 'scanning') return 'warn';
  return 'neutral';
}

/** Bytes as an operator would say them, and "—" while nothing has arrived. */
export function asSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The last path segment. The key is the only name these objects have — filenames never enter it. */
export function shortKey(storageKey: string): string {
  return storageKey.slice(storageKey.lastIndexOf('/') + 1);
}

export function entityLabel(row: MediaLibraryRow): string {
  if (row.entity_type && row.entity_id) return `${row.entity_type} #${row.entity_id}`;
  if (row.pandit_profile_id) return `pandit #${row.pandit_profile_id}`;
  return 'Unattached';
}

/**
 * One tile's image, or an honest explanation of why there is none.
 *
 * Split out because the preview is a per-object query: a grid of twenty-four tiles is
 * twenty-four short-lived signatures, and minting them all up front would mean most of them
 * expiring before anybody scrolled to them.
 */
export function MediaThumb({ row }: { row: MediaLibraryRow }) {
  const hasBytes = row.status !== 'pending' && row.status !== 'deleted';
  const preview = useMediaPreview(hasBytes ? row.id : null, 'thumb');
  const frame = {
    height: 132,
    background: 'var(--c-sunk)',
    border: '1px solid var(--c-border)',
    borderRadius: 'var(--r-tile)',
    display: 'grid',
    placeItems: 'center',
    padding: 8,
    overflow: 'hidden',
    color: 'var(--c-ink3)',
    fontSize: 'var(--fs-admin-colhead)',
    textAlign: 'center' as const,
  };

  if (!hasBytes) return <span style={frame}>Nothing uploaded yet</span>;
  if (preview.isPending) return <span style={frame}>Loading preview…</span>;
  if (preview.isError) return <span style={frame}>Preview unavailable</span>;
  return (
    <span style={frame}>
      <img
        src={preview.data.url}
        alt=""
        style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
      />
    </span>
  );
}

/** The drawer's usage panel — the answer to "can I delete this?", with every page named. */
export function MediaUsagePanel({ mediaId }: { mediaId: number }) {
  const usages = useMediaUsages(mediaId);
  if (usages.isPending) return <LoadingState message="Reading where this is used…" />;
  if (usages.isError)
    return <ErrorState title="Could not read usages" cause={errorMessage(usages.error)} inline />;
  if (usages.data.items.length === 0)
    return (
      <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
        Not used anywhere — safe to delete.
      </p>
    );
  return (
    <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: 'var(--fs-admin-cell)' }}>
      {usages.data.items.map((u) => (
        <li key={usageKey(u)}>
          {u.detail}
          {u.alt_text ? ` — alt: “${u.alt_text}”` : ' — no alt text on this usage'}
        </li>
      ))}
    </ul>
  );
}

/** `temple:12` — the identity a replace selects by. The entity id alone is ambiguous when one
 *  temple uses the same image twice. */
export function usageKey(u: Pick<MediaUsageRow, 'surface' | 'usage_id'>): string {
  return `${u.surface}:${u.usage_id}`;
}

/** How a candidate replacement reads in the picker: its id, its key, what it belongs to. */
export function candidateLabel(row: MediaLibraryRow): string {
  return `#${row.id} · ${shortKey(row.storage_key)} · ${entityLabel(row)}`;
}

/**
 * Re-point the ticked usages at another object, in one request the server applies atomically.
 *
 * The candidates are the library's own `ready` objects in the same prefix — the server refuses
 * anything else (409 `media_not_ready`, 422 `media_prefix_mismatch`), so offering it here would
 * be offering a refusal. Uploading a fresh image happens at the record it belongs to, which binds
 * it to its entity and alt text; once it is scanned it appears in this picker.
 */
export function ReplaceDialog({
  row,
  usages,
  onClose,
}: {
  row: MediaLibraryRow;
  usages: readonly MediaUsageRow[];
  onClose: () => void;
}) {
  const { toast } = useConsole();
  const replace = useReplaceMediaUsages();
  const [ticked, setTicked] = useState<string[]>(() => usages.map(usageKey));
  const [choice, setChoice] = useState('');
  const pool = useMediaLibrary({
    prefix_class: row.prefix_class,
    status: 'ready',
    page: 1,
    page_size: 100,
  });
  const candidates = (pool.data?.items ?? []).filter((m) => m.id !== row.id);
  const chosen = candidates.find((m) => candidateLabel(m) === choice);
  const selected = usages.filter((u) => ticked.includes(usageKey(u)));

  function submit(target: MediaLibraryRow) {
    replace.mutate(
      {
        mediaId: row.id,
        newMediaId: target.id,
        usages: selected.map((u) => ({
          surface: u.surface as MediaUsageRef['surface'],
          usage_id: u.usage_id,
        })),
      },
      {
        onSuccess: (out) => {
          toast({
            tone: 'success',
            message: `Moved ${out.moved} ${out.moved === 1 ? 'usage' : 'usages'} to ${shortKey(target.storage_key)}`,
          });
          onClose();
        },
        onError: (e: unknown) => toast({ tone: 'error', message: errorMessage(e) }),
      },
    );
  }

  return (
    <Modal
      title={`Replace ${shortKey(row.storage_key)}`}
      width={600}
      onClose={onClose}
      actions={
        <>
          <Button size="md" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="md"
            disabled={!chosen || selected.length === 0 || replace.isPending}
            /* Enabled only once a candidate is chosen, so the cast restates the guard above. */
            onClick={() => submit(chosen as MediaLibraryRow)}
          >
            {`Move ${selected.length} ${selected.length === 1 ? 'usage' : 'usages'}`}
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
        <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
          {usages.map((u) => {
            const key = usageKey(u);
            const on = ticked.includes(key);
            return (
              <label
                key={key}
                style={{
                  display: 'flex',
                  gap: 'var(--sp-sm)',
                  alignItems: 'center',
                  fontSize: 'var(--fs-admin-cell)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={on}
                  aria-label={u.detail}
                  onChange={() =>
                    setTicked((t) => (on ? t.filter((x) => x !== key) : [...t, key]))
                  }
                  style={{ width: 16, height: 16 }}
                />
                {u.detail}
              </label>
            );
          })}
        </div>
        {pool.isPending && <LoadingState message="Reading ready objects…" />}
        {pool.isError && (
          <ErrorState title="Could not read the library" cause={errorMessage(pool.error)} inline />
        )}
        {pool.isSuccess && candidates.length === 0 && (
          <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
            No other ready {row.prefix_class} object to replace it with. Upload the new image from
            the record it belongs to; once it has been scanned it appears here.
          </p>
        )}
        {candidates.length > 0 && (
          <Select
            density="admin"
            label="Replace with"
            value={choice}
            placeholder="Choose a ready object"
            options={candidates.map(candidateLabel)}
            onChange={setChoice}
          />
        )}
        <Consequences
          tone="warn"
          items={[
            'Every ticked page moves in one step — if any of them has changed since this drawer opened, none move.',
            'The pages pick up the new file at the next site build. This object is kept until you delete it.',
          ]}
        />
      </div>
    </Modal>
  );
}

/** The asset drawer: preview, facts, usages — and Replace and Delete (admin-24 §3.3). */
export function MediaDrawer({ row, onClose }: { row: MediaLibraryRow; onClose: () => void }) {
  const { scope, toast } = useConsole();
  const usages = useMediaUsages(row.id);
  const remove = useDeleteMedia();
  const [deleting, setDeleting] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const used = usages.data?.items ?? [];
  /* Only a settled, empty read licenses deletion. A pending or failed read is not "unused". */
  const unused = usages.isSuccess && used.length === 0;

  return (
    <DetailPage
      title={shortKey(row.storage_key)}
      code={row.storage_key}
      status={<StatusChip tone={statusTone(row.status)} label={row.status} />}
      onClose={onClose}
      audit="Uploaded through a presigned URL — the server never handled the bytes."
      actions={
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            variant="secondary"
            icon="repeat"
            disabled={used.length === 0}
            onClick={() => setReplacing(true)}
          >
            Replace
          </ScopeAction>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            variant="destructive"
            disabled={!unused}
            onClick={() => setDeleting(true)}
          >
            Delete
          </ScopeAction>
        </div>
      }
    >
      <MediaThumb row={row} />

      <KV
        rows={[
          ['Prefix', row.prefix_class],
          ['Attached to', entityLabel(row)],
          ['Content type', row.content_type],
          ['Declared', asSize(row.declared_bytes)],
          ['Actual', asSize(row.actual_bytes)],
          [
            'Derivatives',
            row.derivatives.length === 0
              ? 'None yet'
              : row.derivatives.map((d) => `${d.variant} ${d.width_px}px`).join(' · '),
          ],
          ['Uploaded', formatDateTime(row.created_at)],
          ...(row.reject_reason ? [['Rejected because', row.reject_reason] as const] : []),
        ]}
      />

      <Section title="Used on" note="Deletion is only safe once this list is empty">
        <MediaUsagePanel mediaId={row.id} />
      </Section>

      {used.length > 0 && (
        <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
          Delete is disabled while the asset is used — replace it instead, and every page picks up
          the new file at the next deploy.
        </p>
      )}

      {replacing && (
        <ReplaceDialog row={row} usages={used} onClose={() => setReplacing(false)} />
      )}

      <ConfirmAction
        open={deleting}
        title={`Delete ${shortKey(row.storage_key)}?`}
        phrase="DELETE"
        tone="error"
        reason
        consequences={[
          'The original and every derivative are removed from storage. This cannot be undone.',
          'The record stays, marked deleted, so the audit trail and old links still resolve.',
        ]}
        onCancel={() => setDeleting(false)}
        onConfirm={(reason: string) => {
          setDeleting(false);
          remove.mutate(
            { mediaId: row.id, reason },
            {
              onSuccess: () => {
                toast({ tone: 'success', message: `${shortKey(row.storage_key)} deleted` });
                onClose();
              },
              onError: (e: unknown) => toast({ tone: 'error', message: errorMessage(e) }),
            },
          );
        }}
        confirmLabel="Delete asset"
      />
    </DetailPage>
  );
}

/**
 * Upload from the library (admin doc 24 §3.2). An image is always minted against the record it
 * belongs to, so the operator names the puja first; temple imagery is uploaded on the temple's
 * record, which also writes its `temple_media` row. Pickers list names, so the name maps to the id.
 */
function LibraryUpload() {
  const { scope } = useConsole();
  const pujas = usePujas({ page: 1, page_size: 200 });
  const [name, setName] = useState('');
  const items = pujas.data?.items ?? [];
  const chosen = items.find((p) => p.name_en === name);
  return (
    <Section title="Upload" note="Every image belongs to a puja: name it, then choose the files. Temple imagery is uploaded on the temple's record.">
      <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
        <Select
          density="admin"
          label="For puja"
          value={name}
          placeholder={pujas.isPending ? 'Reading pujas…' : 'Choose a puja'}
          options={items.map((p) => p.name_en)}
          onChange={setName}
          style={{ maxWidth: 320 }}
        />
        {pujas.isError && <ErrorState title="Could not read the pujas" cause={errorMessage(pujas.error)} />}
        {chosen && (
          <PujaImageUpload
            scope={scope}
            pujaId={chosen.id}
            note={`Minted for ${chosen.name_en}, scanned, then attached on its record once ready.`}
          />
        )}
      </div>
    </Section>
  );
}

export function Media() {
  const [q, setQ] = useUrlSearch('media');
  const [prefix, setPrefix] = useState<string>(PREFIXES[0]);
  const [status, setStatus] = useState<string>(STATUSES[0]);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<MediaLibraryRow | null>(null);

  const library = useMediaLibrary({
    prefix_class: PREFIX_VALUES[prefix],
    status: status === STATUSES[0] ? undefined : status,
    q: q || undefined,
    page,
    page_size: PAGE_SIZE,
  });

  const rows = library.data?.items ?? [];
  const counts = library.data?.counts_by_status;
  const total = library.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function narrow(next: () => void) {
    // Any change of filter starts at page one. Staying on page 4 of a narrower result is an
    // empty screen that looks like "no matches" when the matches are two pages back.
    setPage(1);
    next();
  }

  if (open) return <MediaDrawer row={open} onClose={() => setOpen(null)} />;

  return (
    <>
      <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
        Marketing and catalogue media only. Completion evidence and KYC documents never appear
        here — they are read inside the booking and KYC consoles, where every view is audited.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: 'var(--sp-md)',
        }}
      >
        <StatTile label="Assets" value={counts === undefined ? '—' : total} note="catalogue + pandit" />
        {/* The design's tile. "Safe to delete" is the usages panel's call per asset; this only
            says how many clean objects no temple, puja or profile points at. */}
        <StatTile
          label="Unused"
          value={counts === undefined ? '—' : (library.data?.unused_count ?? 0)}
          note="no page or profile uses it"
        />
        <StatTile
          label="In the scan queue"
          value={counts?.scanning ?? '—'}
          note="not usable until clean"
        />
        <StatTile
          label="Rejected"
          value={counts?.rejected ?? '—'}
          note="reason on the asset"
          tone={(counts?.rejected ?? 0) > 0 ? 'error' : 'default'}
        />
      </div>

      {library.data && <ScanHealth page={library.data} />}

      <LibraryUpload />

      <FilterBar
        q={q}
        onQ={(v) => narrow(() => setQ(v))}
        placeholder="Storage key — press /"
        extra={
          <>
            <Select
              density="admin"
              label="Kind"
              value={prefix}
              options={[...PREFIXES]}
              onChange={(v) => narrow(() => setPrefix(v))}
              style={{ width: 170 }}
            />
            <Select
              density="admin"
              label="Status"
              value={status}
              options={[...STATUSES]}
              onChange={(v) => narrow(() => setStatus(v))}
              style={{ width: 150 }}
            />
          </>
        }
      />

      {library.isPending && <LoadingState message="Reading the media library…" />}
      {library.isError && (
        <ErrorState title="Could not read the media library" cause={errorMessage(library.error)} />
      )}
      {library.isSuccess && rows.length === 0 && (
        <EmptyState
          icon="image"
          title={total === 0 ? 'No media yet' : 'No asset matches this filter'}
          body={
            total === 0
              ? 'Upload catalogue imagery above, or from a temple record, to get started.'
              : 'Widen the filter, or clear it to see everything the library holds.'
          }
          inline
        />
      )}

      {rows.length > 0 && (
        <Section
          title="Library"
          note={`${total} ${total === 1 ? 'asset' : 'assets'} · page ${page} of ${pages}`}
          pad={false}
          actions={
            pages > 1 ? (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page >= pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </>
            ) : undefined
          }
        >
          <div
            style={{
              padding: 'var(--sp-lg)',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 'var(--sp-md)',
              alignItems: 'start',
            }}
          >
            {rows.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setOpen(m)}
                style={{
                  display: 'grid',
                  gap: 6,
                  cursor: 'pointer',
                  minWidth: 0,
                  border: 'none',
                  background: 'none',
                  padding: 0,
                  font: 'inherit',
                  textAlign: 'left',
                }}
              >
                <MediaThumb row={m} />
                <span
                  style={{
                    fontSize: 'var(--fs-admin-colhead)',
                    color: 'var(--c-ink2)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {shortKey(m.storage_key)}
                </span>
                <span
                  style={{
                    fontSize: 'var(--fs-admin-colhead)',
                    color: 'var(--c-ink3)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {entityLabel(m)} · {asSize(m.actual_bytes ?? m.declared_bytes)}
                </span>
                {/* Hugs its text. Stretched to the column, a status chip reads as a progress bar. */}
                <span style={{ justifySelf: 'start', display: 'flex', gap: 'var(--sp-xs)' }}>
                  <StatusChip tone={statusTone(m.status)} label={m.status} />
                  {/* Admin 24 §2: a page using this file describes it with nothing, or with its
                      own storage key. The server decides; the tile only says so. */}
                  {m.alt_text_missing && <StatusChip tone="warn" label="alt text missing" />}
                </span>
              </button>
            ))}
          </div>
        </Section>
      )}

      {library.isSuccess && (
        <p style={{ margin: 0, fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
          Uploading is done from the record the image belongs to — a puja or a temple — so the
          image is bound to its entity and its alt text at the moment it arrives.
        </p>
      )}
    </>
  );
}
